import React, { useEffect, useState } from 'react';
import './EmployeeForm.css';

const emptyInterval = () => ({ checkInTime: '', checkOutTime: '' });

const TimesheetForm = ({ employees = [], timesheet, isEditing, onSubmit, onClose, onCheckConflict }) => {
  const [formData, setFormData] = useState({
    employeeId: '',
    date: '',
    isAbsent: false,
    breakDurationMinutes: 0,
    notes: '',
  });
  const [intervals, setIntervals] = useState([emptyInterval()]);
  const [errors, setErrors] = useState({});
  const [conflict, setConflict] = useState('');

  useEffect(() => {
    if (timesheet) {
      setFormData({
        employeeId: timesheet.employeeId || '',
        date: timesheet.date || '',
        isAbsent: timesheet.isAbsent ?? false,
        breakDurationMinutes: timesheet.breakDurationMinutes || 0,
        notes: timesheet.notes || '',
      });
      if (timesheet.intervals && timesheet.intervals.length > 0) {
        setIntervals(timesheet.intervals.map((i) => ({
          checkInTime: i.checkInTime || '',
          checkOutTime: i.checkOutTime || '',
        })));
      } else if (timesheet.checkInTime || timesheet.checkOutTime) {
        setIntervals([{
          checkInTime: timesheet.checkInTime || '',
          checkOutTime: timesheet.checkOutTime || '',
        }]);
      } else {
        setIntervals([emptyInterval()]);
      }
    }
  }, [timesheet]);

  const validate = () => {
    const newErrors = {};
    if (!formData.employeeId) newErrors.employeeId = 'Employé obligatoire';
    if (!formData.date) newErrors.date = 'Date obligatoire';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const nextValue = type === 'checkbox' ? checked : value;
    setFormData((prev) => ({ ...prev, [name]: nextValue }));
    if (name === 'date' && formData.employeeId && onCheckConflict) {
      onCheckConflict(formData.employeeId, value).then((msg) => setConflict(msg || ''));
    }
  };

  const handleIntervalChange = (index, field, value) => {
    setIntervals((prev) => prev.map((item, i) => (i === index ? { ...item, [field]: value } : item)));
  };

  const addInterval = () => {
    setIntervals((prev) => [...prev, emptyInterval()]);
  };

  const removeInterval = (index) => {
    setIntervals((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== index)));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    try {
      const payload = {
        ...formData,
        intervals: intervals
          .filter((i) => i.checkInTime || i.checkOutTime)
          .map((i, idx) => ({
            sequenceOrder: idx + 1,
            checkInTime: i.checkInTime || null,
            checkOutTime: i.checkOutTime || null,
          })),
      };

      if (payload.isAbsent) {
        payload.intervals = [];
        payload.checkInTime = '';
        payload.checkOutTime = '';
        payload.breakDurationMinutes = 0;
        if (!payload.notes || !payload.notes.trim()) {
          payload.notes = 'Absence';
        }
      } else {
        const first = payload.intervals[0];
        const last = payload.intervals[payload.intervals.length - 1];
        payload.checkInTime = first?.checkInTime || '';
        payload.checkOutTime = last?.checkOutTime || '';
      }

      await onSubmit(payload);
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message || 'Erreur lors de l\'enregistrement';
      setErrors({ submit: errorMessage });
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isEditing ? 'Modifier un pointage' : 'Nouveau pointage'}</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit} className="employee-form">
          <div className="form-row">
            <div className="form-group">
              <label>Employé *</label>
              <select
                name="employeeId"
                value={formData.employeeId}
                onChange={handleChange}
                className={errors.employeeId ? 'error' : ''}
              >
                <option value="">Sélectionner</option>
                {employees.map((emp) => (
                  <option key={emp.id} value={emp.id}>
                    {emp.nom} {emp.prenom}
                  </option>
                ))}
              </select>
              {errors.employeeId && <span className="error-message">{errors.employeeId}</span>}
            </div>
            <div className="form-group">
              <label>Date *</label>
              <input
                type="date"
                name="date"
                value={formData.date}
                onChange={handleChange}
                className={errors.date ? 'error' : ''}
              />
              {errors.date && <span className="error-message">{errors.date}</span>}
              {conflict && <span className="warning-text">{conflict}</span>}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <label style={{ marginBottom: 0 }}>
                <input
                  type="checkbox"
                  name="isAbsent"
                  checked={!!formData.isAbsent}
                  onChange={handleChange}
                  style={{ marginRight: 8 }}
                />
                Marquer comme absent
              </label>
            </div>
          </div>

          {!formData.isAbsent && (
            <div className="form-group">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <label style={{ marginBottom: 0 }}>Entrées / sorties</label>
                <button type="button" className="btn btn-secondary" onClick={addInterval} style={{ padding: '4px 10px' }}>
                  + Ajouter une période
                </button>
              </div>
              {intervals.map((interval, index) => (
                <div key={index} className="form-row" style={{ alignItems: 'end', marginBottom: 8 }}>
                  <div className="form-group">
                    <label>Entrée {index + 1}</label>
                    <input
                      type="time"
                      value={interval.checkInTime}
                      onChange={(e) => handleIntervalChange(index, 'checkInTime', e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label>Sortie {index + 1}</label>
                    <input
                      type="time"
                      value={interval.checkOutTime}
                      onChange={(e) => handleIntervalChange(index, 'checkOutTime', e.target.value)}
                    />
                  </div>
                  {intervals.length > 1 && (
                    <div className="form-group">
                      <button type="button" className="btn btn-secondary" onClick={() => removeInterval(index)}>
                        Supprimer
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          <div className="form-row">
            <div className="form-group">
              <label>Pause (minutes)</label>
              <input
                type="number"
                name="breakDurationMinutes"
                value={formData.breakDurationMinutes}
                onChange={handleChange}
                min="0"
                disabled={!!formData.isAbsent}
              />
            </div>
          </div>

          <div className="form-group">
            <label>Notes</label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows="3"
            />
          </div>

          {errors.submit && <div className="error-message" style={{ marginBottom: '1rem', color: '#dc3545' }}>{errors.submit}</div>}
          <div className="form-actions">
            <button type="button" onClick={onClose} className="btn btn-secondary">Annuler</button>
            <button type="submit" className="btn btn-primary">
              {isEditing ? 'Mettre à jour' : 'Enregistrer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TimesheetForm;
